import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import {
  engagementSurveys,
  type ProgramSurveyAnswer,
  type ProgramSurveyDetail,
  type ProgramSurveyItem,
  type ProgramSurveySummary,
  type SaveProgramSurvey,
} from 'services/api/engagementSurveysClient';
import './ProgramSurveys.css';

const dateLabel = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  }).format(date);
};

const statusLabel = { available: 'Ready to complete', submitted: 'Submitted', upcoming: 'Opens soon', closed: 'Closed' };

const answerPresent = (value: ProgramSurveyAnswer | undefined) => (
  value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)
);

type UnsavedDraft = { answers: Record<string, ProgramSurveyAnswer>; version: number };

function Question({ item, value, disabled, onChange }: {
  item: ProgramSurveyItem;
  value: ProgramSurveyAnswer | undefined;
  disabled: boolean;
  onChange: (value: ProgramSurveyAnswer) => void;
}) {
  const inputId = `program-survey-${item.id}`;
  if (item.type === 'text') return (
    <div className="program-survey-question">
      <label htmlFor={inputId}>{item.label}{item.required ? ' *' : ' (optional)'}</label>
      <Textarea id={inputId} value={typeof value === 'string' ? value : ''} disabled={disabled}
        maxLength={item.max_length ?? 2000} rows={3} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
  const minimum = item.min ?? 1;
  const maximum = item.max ?? 7;
  const options = item.options?.length ? item.options : Array.from(
    { length: Math.max(0, Math.min(21, maximum - minimum + 1)) },
    (_, index) => ({ value: String(index + minimum), label: String(index + minimum) }),
  );
  return (
    <fieldset className="program-survey-question" disabled={disabled}>
      <legend>{item.label}{item.required ? ' *' : ' (optional)'}</legend>
      <div className={`program-survey-options ${item.type === 'rating' ? 'program-survey-rating' : ''}`}>
        {options.map((option) => (
          <label key={option.value} className={String(value) === option.value ? 'is-selected' : ''}>
            <input type="radio" name={inputId} value={option.value} checked={String(value) === option.value}
              onChange={() => onChange(item.type === 'rating' ? Number(option.value) : option.value)} />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {item.min_label || item.max_label ? <div className="program-survey-scale"><span>{item.min_label}</span><span>{item.max_label}</span></div> : null}
    </fieldset>
  );
}

function SurveyForm({ id, onSaved, drafts }: { id: string; onSaved: () => void; drafts: Map<string, UnsavedDraft> }) {
  const [detail, setDetail] = useState<ProgramSurveyDetail | null>(null);
  const [answers, setAnswers] = useState<Record<string, ProgramSurveyAnswer>>({});
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const currentRequest = useRef<{ signature: string; payload: SaveProgramSurvey } | null>(null);
  const alive = useRef(true);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const next = await engagementSurveys.get(id);
      if (!alive.current || request !== generation.current) return;
      setDetail(next);
      const local = drafts.get(id);
      const savedVersion = next.response?.version ?? 0;
      const changed = Boolean(local && local.version !== savedVersion);
      setAnswers(local?.answers ?? next.response?.answers ?? {});
      setVersion(local?.version ?? savedVersion);
      setConflict(changed);
      if (changed) setError('This check-in changed in another session. Your answers below are still here. Copy anything you need before loading the saved version.');
      setNotice(null);
      currentRequest.current = null;
    } catch {
      if (alive.current && request === generation.current) setError('We could not open this check-in. Please try again.');
    } finally {
      if (alive.current && request === generation.current) setLoading(false);
    }
  }, [id, drafts]);
  useEffect(() => {
    alive.current = true;
    void load();
    return () => { alive.current = false; generation.current += 1; };
  }, [load]);

  const save = async (submit: boolean) => {
    if (!detail || busy || conflict) return;
    const missing = detail.items.filter((item) => item.required && !answerPresent(answers[item.id]));
    if (submit && missing.length) {
      setError('Please answer the required questions before submitting. You can save an unfinished draft.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    const signature = JSON.stringify({ id, version, answers, submit });
    if (currentRequest.current?.signature !== signature) currentRequest.current = {
      signature,
      payload: { request_id: crypto.randomUUID(), expected_version: version, answers: { ...answers }, submit },
    };
    try {
      const response = await engagementSurveys.save(id, currentRequest.current.payload);
      if (!alive.current) return;
      setVersion(response.version);
      drafts.delete(id);
      setDetail((previous) => previous ? {
        ...previous, response, status: response.submitted_at ? 'submitted' : previous.status,
      } : previous);
      currentRequest.current = null;
      setNotice(response.submitted_at ? 'Thank you. Your check-in has been submitted.' : 'Your draft is saved. Submit it when you are ready.');
      onSaved();
    } catch (failure) {
      if (!alive.current) return;
      const status = (failure as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        setConflict(true);
        setError('This check-in changed in another session. Your answers below are still here. Copy anything you need before loading the saved version.');
      } else {
        setError('We could not confirm the save. Your answers are still here. Try again to safely check the same request.');
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  if (loading) return <p role="status" className="program-survey-loading">Opening your check-in…</p>;
  if (!detail) return <div role="alert"><p>{error}</p><Button size="auto" onClick={() => void load()}>Try again</Button></div>;
  const submitted = Boolean(detail.response?.submitted_at);
  const editable = detail.status === 'available' && !submitted;
  return (
    <section className="program-survey-panel" aria-labelledby="program-survey-title">
      <div className="program-survey-heading">
        <p className="program-survey-eyebrow">{detail.engagement_name}</p>
        <h2 id="program-survey-title">{detail.title}</h2>
        {detail.session_title ? <p>{detail.session_title}</p> : null}
        <p className="program-survey-date">{statusLabel[detail.status]} · Closes {dateLabel(detail.closes_at)}</p>
      </div>
      <div className="program-survey-privacy"><ShieldCheck size={20} aria-hidden="true" /><p>{detail.privacy_notice}</p></div>
      {detail.status === 'upcoming' ? <p>Available from {dateLabel(detail.opens_at)}.</p> : null}
      {detail.status === 'closed' && !submitted ? <p>This check-in has closed. Any saved draft is shown below.</p> : null}
      <form onSubmit={(event) => { event.preventDefault(); void save(true); }}>
        <div className="program-survey-questions">
          {detail.items.map((item) => <Question key={item.id} item={item} value={answers[item.id]}
            disabled={!editable || busy || conflict}
            onChange={(value) => {
              setAnswers((previous) => {
                const next = { ...previous, [item.id]: value };
                drafts.set(id, { answers: next, version });
                return next;
              });
              setNotice(null);
            }} />)}
        </div>
        {error ? <div role="alert" className="program-survey-error"><p>{error}</p>{conflict ? <Button type="button" variant="outline" size="auto" onClick={() => { drafts.delete(id); void load(); }}>Load saved version</Button> : null}</div> : null}
        {notice ? <p role="status" className="program-survey-notice">{notice}</p> : null}
        {submitted ? <p className="program-survey-notice"><Check size={18} aria-hidden="true" /> Submitted {dateLabel(detail.response?.submitted_at ?? '')}</p> : null}
        {editable ? <div className="program-survey-actions">
          <Button type="button" variant="outline" size="auto" disabled={busy || conflict} onClick={() => void save(false)}>Save draft</Button>
          <Button type="submit" size="auto" disabled={busy || conflict}>{busy ? 'Saving…' : 'Submit check-in'}<ArrowRight size={16} /></Button>
        </div> : null}
      </form>
    </section>
  );
}

export default function ProgramSurveys() {
  const [params, setParams] = useSearchParams();
  const [surveys, setSurveys] = useState<ProgramSurveySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const alive = useRef(true);
  const generation = useRef(0);
  const drafts = useRef(new Map<string, UnsavedDraft>());
  const selectedId = params.get('survey');
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setError(false);
    try {
      const next = await engagementSurveys.list();
      if (alive.current && request === generation.current) setSurveys(next);
    } catch {
      if (alive.current && request === generation.current) setError(true);
    } finally {
      if (alive.current && request === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => { alive.current = false; generation.current += 1; };
  }, [refresh]);
  return (
    <main className="program-surveys">
      <Link to="/home" className="program-survey-back"><ArrowLeft size={16} /> Home</Link>
      <header><p className="program-survey-eyebrow">Your program</p><h1>Program check-ins</h1><p>Share how things are going at each stage of your program.</p></header>
      {error ? <div role="alert" className="program-survey-error"><p>We could not check your available surveys.</p><Button size="auto" variant="outline" onClick={() => void refresh()}>Try again</Button></div> : null}
      {loading ? <p role="status">Checking your program…</p> : null}
      {!loading && !error && !surveys.length && !selectedId ? <p className="program-survey-empty">You have no program check-ins assigned right now.</p> : null}
      <div className={selectedId ? 'program-survey-layout' : ''}>
        {surveys.length ? <nav className="program-survey-list" aria-label="Your check-ins">
          {surveys.map((survey) => <button key={survey.id} type="button" aria-current={selectedId === survey.id ? 'page' : undefined}
            onClick={() => setParams({ survey: survey.id })}>
            <span><strong>{survey.title}</strong><small>{survey.engagement_name}</small></span>
            <span className="program-survey-status">{statusLabel[survey.status]}{survey.status === 'submitted' ? <Check size={16} aria-hidden="true" /> : null}</span>
          </button>)}
        </nav> : null}
        {selectedId ? <SurveyForm key={selectedId} id={selectedId} drafts={drafts.current} onSaved={() => void refresh()} /> : null}
      </div>
    </main>
  );
}
